<template>
	<div class="module overview-table">
		<div
			v-for="(detail, index) in overviewDetails"
			:key="index"
			class="display-flex justify-content-space-between p-100"
			:class="{ bt: index !== 0 }"
			:data-test="`overview-table-${detail.key}`"
		>
			<span class="text-color-content-secondary" v-html="detail.label"></span>
			<div>
				<BaseTooltip v-if="detail.tooltipText">
					<template #trigger
						><span class="font-weight-bold">{{ detail.value }}</span></template
					>
					<RichTextComponent v-if="detail.tooltipText" :blocks="detail.tooltipText" />
				</BaseTooltip>
				<span v-else>{{ detail.value }}</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { currency, date } from '@filters/shared-filters';
import { computed } from 'vue';
import { Fund } from 'types/funds';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityRichTextBlock } from 'types/sanity';

interface Props {
	fund: Fund;
}

const props = defineProps<Props>();

const overviewDetails = computed(
	(): Array<{
		key: string;
		label: string;
		value: string;
		tooltipText?: Array<SanityRichTextBlock>;
	}> => {
		const details = [
			{
				key: 'category',
				label: 'Fund category',
				value: props.fund.category?.name ?? '',
				tooltipText: props.fund.category?.description
			},
			{
				key: 'objective',
				label: 'Objective',
				value: props.fund.objective?.name ?? '',
				tooltipText: props.fund.objective?.description
			},
			{
				key: 'inceptionDate',
				label: 'Inception date',
				value: props.fund.inceptionDate ? date(`${props.fund.inceptionDate}`) : ''
			},
			{
				key: 'navPerShare',
				label: props.fund.navPerShare ? 'Current NAV per share' : 'Current price per share',
				value: props.fund.navPerShare ? currency(props.fund.navPerShare) : '$10.00'
			},
			{
				key: 'reservationWindow',
				label: 'Next available window',
				value: props.fund.reservationWindow?.reservationWindow.name ?? ''
			}
		];

		return details.filter(
			(detail) => !!detail.value && !props.fund.fundDetails?.hiddenOverviewTableFields.includes(detail.key)
		);
	}
);
</script>
